// @ts-check
// =============================================================================
// js/screens/rewards.js — booster packs: earn, open, unlock card art.
//
// WHAT THIS IS (plan/rewards.md; old RenderRewards :3082 + RevealPack :3163)
//   The pack shelf. Wins (and pity losses) earn packs server-side; packs can
//   also arrive MID-SESSION over 'User Update' (we subscribe and repaint).
//
// TIER BEHAVIOR (the paywall, kept exactly)
//   Paid:  tap a pack -> "Collect Pack" opens it for real (loot art ids move
//          into the collection; fresh cards+collection come back).
//   Cheap: packs bank unopened — loot shows as blurred "Hidden Reward" cards
//          linking to the supporter pitch.
//   Guest: no packs at all (nothing persists).
//
//   Viewing the shelf marks the nav badge read (rewardsSeen, old :3084).
// =============================================================================

import { store, subscribe, setSlice } from '../core/store.js';
import { request } from '../net/http.js';
import { GENDERS } from '../app/genders.js';
import { shuffle } from '../rules.js';
import { refreshCardData } from '../render/card-tile.js';
import { clearImageCache } from '../render/image-cache.js';
import { artImgAttrs } from '../net/cdn.js';
import { showUpsell } from '../ui/upsell.js';
import { escapeHtml } from '../ui/modal.js';
import { toast } from '../ui/toast.js';

/** Card art bucket (finished loot art is served pre-rendered from there). */
const BUCKET = 'https://storage.googleapis.com/deckbiter/';

/** Guards double-open taps (old awaitingReward). */
let awaitingReward = false;

/** @type {(() => void)|null} */
let stopUserData = null;

/** @type {HTMLElement|null} */
let screenEl = null;

/**
 * Render the rewards shelf.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	screenEl = document.createElement('div');
	screenEl.className = 'screen';
	container.appendChild(screenEl);
	paint();
	// New packs can land mid-session ('User Update' -> userData notify).
	stopUserData = subscribe('userData', paint);
}

/** Stop listening for pack drops. */
export function destroy() {
	stopUserData?.();
	stopUserData = null;
	screenEl = null;
}

/** Repaint the shelf. */
function paint() {
	if (!screenEl || !store.userData) return;
	const { userData } = store;
	const packs = userData.collection?.packs || [];

	// Mark the badge read (old rewardsSeen; nav shell reads this).
	localStorage.setItem('rewardsSeen', String(packs.length));
	setSlice('unopenedPacks', 0);

	// Header + a top-right tally of art styles: how many you've UNLOCKED vs how
	// many are UNLOCKABLE under your current content prefs (see computeStyleTally).
	const tally = computeStyleTally();
	let html = `<div class="rewards-head">
		<h1 class="screen-title">Rewards</h1>
		<div class="rewards-tally" title="Art styles you've unlocked out of everything a pack can drop for your content settings">
			🎨 <b>${tally.unlocked}</b> / ${tally.unlockable} styles
		</div>
	</div>`;
	if (!packs.length) {
		html += `<div class="empty-state">No packs yet — win duels to earn boosters!</div>`;
		screenEl.innerHTML = html;
		return;
	}

	packs.forEach((pack, packIndex) => {
		const lootArts = resolveLootArts(pack);
		html += `<div id="pack-${packIndex}" class="pack">
			<img src="/boosters/${encodeURIComponent(pack.name)}.png" class="booster"
				data-pack-time="${pack.time}" alt="${escapeHtml(pack.name)}" title="Open ${escapeHtml(pack.name)}">`;
		for (const src of lootArts) {
			if (userData.realUser) {
				html += `<img ${artImgAttrs(BUCKET + src)} class="card" alt="Card art">`;
			} else {
				// Cheap tier: banked — blurred behind the Hidden Reward face.
				html += `<img src="/boosters/Hidden Reward.png" class="card js-hidden-loot"
					style="background-image:url('${BUCKET}${src}');background-size:cover;background-position:center;cursor:pointer;"
					alt="Hidden reward">`;
			}
		}
		html += `</div><br>`;
	});
	screenEl.innerHTML = html;

	for (const booster of screenEl.querySelectorAll('.booster')) {
		// Either tier reveals the loot with the slide-out. Non-paid then gets the
		// supporter pitch AFTER the tween (revealPack) — the cards fan out, then
		// the popup explains why the art stays locked.
		booster.addEventListener('click', () => void revealPack(/** @type {HTMLElement} */ (booster)));
	}
	for (const hidden of screenEl.querySelectorAll('.js-hidden-loot')) {
		hidden.addEventListener('click', () => showUpsell('openPack'));
	}
}

/**
 * Tally of card-art STYLES for the top-right badge:
 *   unlockable — every pack-droppable style (chance not 0/100, the same set the
 *                economy rolls) that passes the player's content prefs, so a
 *                censored category isn't counted as "missing";
 *   unlocked   — those the player actually owns (art_id in their collection).
 * A "style" is one newart entry (art_id), not its per-gender image variants.
 * @returns {{ unlocked: number, unlockable: number }}
 */
function computeStyleTally() {
	const { userData } = store;
	const censor = userData?.settings?.censor || {};
	const owned = userData?.collection?.cards || {};
	let unlockable = 0;
	let unlocked = 0;
	(store.cards || []).forEach((card, id) => {
		for (const art of card?.newarts || []) {
			const chance = String(art?.chance);
			if (chance === '0' || chance === '100') continue; // default/credit, not droppable
			if ((art.nude && censor.nude) || (art.smut && censor.smut)
				|| (art.vore && censor.vore) || (art.gore && censor.gore)) continue; // hidden by prefs
			unlockable++;
			if (owned[id]?.includes(art.art_id)) unlocked++;
		}
	});
	return { unlocked, unlockable };
}

/**
 * Which art images a pack's loot shows, filtered by censor + gender settings
 * (old :3098–3136 — capped at 4 per loot line, shuffled).
 * @param {any} pack
 * @returns {string[]}  Bucket-relative "Card_….png" paths.
 */
function resolveLootArts(pack) {
	const { userData } = store;
	/** @type {string[]} */
	const arts = [];
	for (const loot of pack.loot || []) {
		const art = store.cards[loot.card]?.newarts?.find((item) => item.art_id == loot.art);
		if (!art) continue;
		// Censored content stays hidden even inside packs.
		if (
			(art.nude && userData.settings.censor.nude) ||
			(art.vore && userData.settings.censor.vore) ||
			(art.gore && userData.settings.censor.gore) ||
			(art.smut && userData.settings.censor.smut)
		) continue;

		/** @type {string[]} */
		const variants = [];
		for (const gender of GENDERS) {
			if (art[gender] && (gender === 'generic' || userData.settings.gender[gender.replace('_x_', '_')])) {
				variants.push('Card_' + String(art[gender]).replace('.jpg', '.png'));
			}
		}
		arts.push(...shuffle(variants.slice(0, 4)));
	}
	return arts;
}

/**
 * Open a pack (old RevealPack :3163): vibrate, "Collect Pack" (paid), then
 * slide the loot out.
 * @param {HTMLElement} booster
 */
async function revealPack(booster) {
	if (awaitingReward) return;
	awaitingReward = true;
	const { userData } = store;

	try {
		if (userData.realUser) {
			booster.classList.add('vibrate');
			const returned = await request('Collect Pack', {
				packTime: Number(booster.getAttribute('data-pack-time')),
			});
			if (!returned) {
				toast('Could not open the pack.', { error: true });
				booster.classList.remove('vibrate');
				return;
			}
			if (returned.cards) {
				// Fresh collection + card list (new arts now owned).
				userData.cards = returned.cards;
				userData.collection = returned.collection;
				setSlice('cards', returned.cards);
				await refreshCardData();
				await clearImageCache();
			}
		}
		// The reveal: cards slide out from behind the booster. (No repaint here
		// — it would cut the animation short; the shelf refreshes on the next
		// visit or pack drop, exactly like the old client.)
		booster.parentElement?.classList.add('reveal');
		booster.remove();
		// Non-paid: the loot fans out but the art stays locked — once the
		// slide-out tween (0.6s, rewards.css) settles, pitch the supporter perk.
		if (!userData.realUser) {
			setTimeout(() => showUpsell('openPack'), 700);
		}
	} finally {
		awaitingReward = false;
	}
}
